import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import DateInput from "@components/fields/DateInput";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import MedicalConditionSelect from "@components/fields/MedicalConditionSelect";
import Select from "@components/fields/Select";
import Form from "@components/forms/Form";
import FormCondition from "@components/forms/FormCondition";
import useCreateDiagnosis from "@hooks/mutations/useCreateDiagnosis";
import {
  DiagnosisFragment as Diagnosis,
  DiagnosisControlEnum,
  DiagnosisSeverityEnum,
  DiagnosisUpdateDocument,
  DiagnosisUpdateMutation,
  DiagnosisUpdateMutationVariables
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { optionsForDiagnosisControl, optionsForDiagnosisSeverity } from "@utils/optionUtils";

type FormData = {
  conditionOther?: string | null;
  control: DiagnosisControlEnum | null;
  date: string | null;
  medicalConditionId?: string | null;
  severity: DiagnosisSeverityEnum | null;
};

type Props = {
  diagnosis?: Diagnosis;
  journeyId: string;
  onCancel?: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const DiagnosisForm = ({ diagnosis, journeyId, onCancel, onError, onSuccess }: Props) => {
  const intl = useIntl();

  let defaultDate = null;
  if (diagnosis?.date) {
    defaultDate = new Date(diagnosis.date).toISOString().split("T")[0];
  }

  const defaultValues: FormData = {
    conditionOther: diagnosis?.conditionOther ?? null,
    control: diagnosis?.control ?? null,
    date: defaultDate,
    medicalConditionId: diagnosis?.medicalCondition?.id ?? null,
    severity: diagnosis?.severity ?? null
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const createDiagnosis = useCreateDiagnosis(journeyId);
  const [updateDiagnosis] = useMutation<DiagnosisUpdateMutation, DiagnosisUpdateMutationVariables>(
    DiagnosisUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      conditionOther: yup.string().when(["medicalConditionId"], ([medicalConditionId], schema) => {
        if (medicalConditionId === "other" || medicalConditionId === null) {
          return schema.required(intl.formatMessage({ id: "errors.diagnosis.conditionOther.blank" }));
        }
        return schema.nullable();
      }),
      control: yup.string().nullable().defined().oneOf(Object.values(DiagnosisControlEnum)),
      date: yup.string().nullable().defined(),
      medicalConditionId: diagnosis
        ? yup.string().nullable()
        : yup.string().required(intl.formatMessage({ id: "errors.diagnosis.medicalConditionId.blank" })),
      severity: yup.string().nullable().defined().oneOf(Object.values(DiagnosisSeverityEnum))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    const values = form.getValues();

    if (values.date === "") {
      values.date = null;
    }

    if (diagnosis?.id) {
      return updateDiagnosis({
        variables: {
          ...values,
          id: diagnosis.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.diagnosisUpdate,
          intl,
          onError,
          onSuccess: () => {
            form.reset();
            onSuccess();
          },
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.diagnosis"
        });
      });
    }

    return createDiagnosis({
      variables: {
        ...values,
        conditionOther: form.getValues().medicalConditionId === "other" ? form.getValues().conditionOther : null,
        control: form.getValues().control as DiagnosisControlEnum,
        journeyId,
        medicalConditionId:
          form.getValues().medicalConditionId === "other" ? null : form.getValues().medicalConditionId,
        severity: form.getValues().severity as DiagnosisSeverityEnum
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.diagnosisCreate,
        intl,
        onSuccess: () => {
          form.reset();
          onSuccess();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.diagnosis"
      });
    });
  }, [diagnosis?.id, createDiagnosis, form, journeyId, updateDiagnosis, intl, onError, onSuccess]);

  const controlOptions = optionsForDiagnosisControl(intl);
  const severityOptions = optionsForDiagnosisSeverity(intl);

  const initialMedicalConditionOption = useMemo(() => {
    if (diagnosis) {
      if (diagnosis?.medicalCondition) {
        return { label: diagnosis.medicalCondition.consumerName, value: diagnosis.medicalCondition.id };
      }
      return { label: intl.formatMessage({ id: "dictionary.other" }), value: "other" };
    }
    return { label: "", value: "" };
  }, [diagnosis, intl]);

  const medicalConditionId = form.watch("medicalConditionId");

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <FormCondition<FormData> condition={!diagnosis} fieldName="medicalConditionId" form={form}>
        <MedicalConditionSelect
          control={form.control}
          error={form.formState.errors.medicalConditionId}
          initialOption={initialMedicalConditionOption}
          label={intl.formatMessage({ id: "forms.diagnosis.medicalConditionIdLabel" })}
          name="medicalConditionId"
          required
        />
      </FormCondition>
      <FormCondition<FormData>
        condition={!diagnosis && medicalConditionId === "other"}
        fieldName="conditionOther"
        form={form}
      >
        <Input<FormData>
          error={form.formState.errors.conditionOther}
          label={intl.formatMessage({
            id: "forms.diagnosis.conditionOtherLabel"
          })}
          register={form.register("conditionOther")}
          required
        />
      </FormCondition>

      <DateInput<FormData>
        control={form.control}
        error={form.formState.errors.date}
        label={intl.formatMessage({ id: "forms.diagnosis.dateLabel" })}
        pastOnly
        register={form.register("date")}
      />
      <Select<FormData>
        control={form.control}
        error={form.formState.errors.control}
        label={intl.formatMessage({ id: "forms.diagnosis.controlLabel" })}
        name="control"
        options={controlOptions}
      />
      <Select<FormData>
        control={form.control}
        error={form.formState.errors.severity}
        label={intl.formatMessage({ id: "forms.diagnosis.severityLabel" })}
        name="severity"
        options={severityOptions}
      />
      <FormButtons hookForm={form} onCancel={onCancel} saveKey="dictionary.save" />
    </Form>
  );
};

export default DiagnosisForm;
